import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { UntypedFormControl, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import cloneDeep from 'lodash-es/cloneDeep';
import { Store } from '@ngxs/store';
import { Account } from '@core/models';
import { FeatureFlag } from '@core/enums';
import { AccountState } from '@state/account/state';
import { Project } from 'app/modules/projects/projects.types';
import { PanelDef } from 'app/shared/components/panels/panels.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { MenuItem } from 'app/modules/projects/details/dashboard/dashboard-header/dashboard-header.types';

type DashboardHeader = Array<string>;
type Tab = { id: string; name: string; layout?: any[]; panels?: any[] };
type Dashboard = { header: DashboardHeader; tabs: Tab[] };
type TabAddArgs = { tabName: string; menuItemKeys: DashboardHeader };

@Component({
  selector: 'properties-header-menu',
  templateUrl: './properties-header-menu.component.html',
  styleUrls: ['./properties-header-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertiesHeaderMenuComponent extends OnDestroyMixin {
  @Input() currentTabId: number;
  @Input() propertyRoute: string;
  @Input() editMode: boolean = false;
  @Input() property: Project;
  @Input() account: Account;
  @Input() userRole: string;
  @Input() set dashboardDef(value: Dashboard) {
    this.dashboard = value;
    this.loadMenuItems(value);
  }
  @Output() onTabOrderChanged: EventEmitter<DashboardHeader> = new EventEmitter<DashboardHeader>();
  @Output() onTabAdded: EventEmitter<TabAddArgs> = new EventEmitter<TabAddArgs>();
  @Output() onTabTitleEdit: EventEmitter<MenuItem> = new EventEmitter<MenuItem>();
  @Output() onTabRemoved: EventEmitter<MenuItem> = new EventEmitter<MenuItem>();

  @ViewChild('tabPanel') private tabPanel: TemplateRef<any>;
  @ViewChild('tabPanelOrigin') private tabPanelOrigin: MatButton;

  availablePanels: PanelDef<any>[] = [];
  availablePostalPanels: PanelDef<any>[] = [];

  header: DashboardHeader;
  menuItems: MenuItem[] = [];
  tabName = new UntypedFormControl('', [Validators.required]);

  private tabPanelOverlayRef: OverlayRef;
  private tabPanelTemplatePortal: TemplatePortal;
  private dashboard: Dashboard;

  get menuItemKeys(): string[] {
    return this.menuItems.map(({ id, displayText }) => id ?? displayText);
  }

  get hasAssetManagementFeature(): boolean {
    return this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.ASSET_MANAGEMENT));
  }

  constructor(
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
    private _changeDetectorRef: ChangeDetectorRef,
    private _propertiesService: PropertiesService,
    private store: Store,
  ) {
    super();
    this.availablePanels = [
      { type: 'contacts', title: 'Contacts', meta: {} },
      { type: 'unit_form', title: 'Unit Form', meta: { fields: [] } },
      { type: 'form', title: 'Form', meta: { fields: [] } },
      { type: 'images', title: 'Gallery', meta: {} },
      { type: 'matrix', title: 'Tables', meta: { columns: [], fields: [] } },
      { type: 'table', title: 'Related Objects Table', meta: {} },
      { type: 'roles', title: 'Roles', meta: { fields: [] } },
      { type: 'unit_lease', title: 'Rent Roll', meta: {} },
    ];

    if (this.hasAssetManagementFeature) {
      this.availablePanels.push({ type: 'chart', title: 'Chart', meta: {} });
      this.availablePanels.push({ type: 'financial_report', title: 'Financial Report', meta: {} });
    }

    this.availablePostalPanels = [
      { type: 'contacts', title: 'Contacts', meta: {} },
      { type: 'unit_lease', title: 'Rent Roll', meta: {} },
      { type: 'unit_form', title: 'Unit Form', meta: { fields: [] } },
      { type: 'form', title: 'Form', meta: { fields: [] } },
      { type: 'images', title: 'Gallery', meta: {} },
      { type: 'matrix', title: 'Tables', meta: { columns: [], fields: [] } },
      { type: 'table', title: 'Related Objects Table', meta: {} },
      { type: 'roles', title: 'Roles', meta: { fields: [] } },
    ];
  }

  ngOnChanges() {
    this.menuItems = [
      {
        displayText: 'Overview',
        link: `${this.propertyRoute}/overview`,
        linkOptions: { exact: true },
        canShow: true,
        active: !this.currentTabId,
      },
    ].filter(({ canShow }) => canShow);

    if (this.dashboard) {
      this.loadMenuItems(this.dashboard);
    }
  }

  drop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.menuItems, event.previousIndex, event.currentIndex);
      this.menuItems = [...this.menuItems];
      this.onTabOrderChanged.emit(this.menuItemKeys);
    }
  }

  openTabPanel(): void {
    this.tabPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.tabPanelOrigin._elementRef.nativeElement)
        .withPositions([{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }]),
    });

    this.tabPanelOverlayRef
      .attachments()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.tabPanelOverlayRef.overlayElement.querySelector('input').focus());

    this.tabPanelTemplatePortal = new TemplatePortal(this.tabPanel, this.viewContainerRef);

    this.tabPanelOverlayRef.attach(this.tabPanelTemplatePortal);

    this.tabPanelOverlayRef
      .backdropClick()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.closeTabPanel());
  }

  addTab(): void {
    if (!this.tabName.errors) {
      this.onTabAdded.emit({ tabName: this.tabName.value, menuItemKeys: this.menuItemKeys });
      this.closeTabPanel();
    }
  }

  public editTabTitle(menuItem: MenuItem): void {
    this.onTabTitleEdit.emit(menuItem);
  }

  public removeTab(menuItem: MenuItem): void {
    this.onTabRemoved.emit(menuItem);
  }

  menuItemTrackBy(index: number, item: MenuItem): string {
    return item.link;
  }

  addPanel(panelDef: PanelDef<any>): void {
    const clonedPanelDef = cloneDeep(panelDef);
    this._propertiesService.addPanelDefinition(clonedPanelDef);
  }

  getPanels(): PanelDef<any>[] {
    if (this.account?.subdomain !== 'postal') {
      return this.account.subdomain !== 'platformv'
        ? this.availablePanels.filter(({ type }) => type !== 'nearby')
        : this.availablePanels;
    } else {
      return this.availablePostalPanels;
    }
  }

  private closeTabPanel(): void {
    if (this.tabPanelOverlayRef?.hasAttached()) this.tabPanelOverlayRef.detach();
    if (this.tabPanelTemplatePortal?.isAttached) this.tabPanelTemplatePortal.detach();
    this.tabName.setValue('');
  }

  private loadMenuItems(value: Dashboard): void {
    if (value?.header) {
      this.header = value.header;
    }

    const unorderedMenuItems = this.menuItems
      .filter(({ id }) => !id)
      .concat(
        value?.tabs?.map(
          ({ id, name }: Tab) =>
            ({
              id: id,
              displayText: name,
              link: `${this.propertyRoute}/tabs/${id}`,
              linkOptions: { exact: false },
              canShow: true,
              active: this.currentTabId?.toString() == id,
            }) as MenuItem,
        ) || [],
      );

    if (this.header) {
      this.menuItems = this.header.map((menuItemKey) => {
        return unorderedMenuItems.find(({ id, displayText }) => displayText === menuItemKey || id === menuItemKey);
      });
    } else {
      this.menuItems = unorderedMenuItems;
    }

    this.menuItems = this.menuItems.filter((menuItem) => menuItem);
    this._changeDetectorRef.markForCheck();
  }
}
